import { Component, Input, OnInit, Renderer2 } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';

import { FormControlChildAbstract } from '~/component/form/abstracts/control-child.abstract';
import { Type } from '~/component/form/input/enums/type.enum';
import { FormControlService } from '~/core/services/form/form-control.service';
import { dateFormatValidator } from '~/core/validators/date/date-format.validator';
import { dateRangeValidator } from '~/core/validators/date/date-range.validator';
import { timeFormatValidator } from '~/core/validators/date/time-format.validator';

import { DatetimeVariantEnum } from './enums/variant.enum';
import { DateTimeInterface } from './interfaces/datetime.interface';
import { ParamethorService } from '~/paramethor/paramethor.service';
import { ParamethorNameEnum } from '~/paramethor/names.enum';
import { AutocompleteSuggestion } from '../autocomplete/interfaces/suggestion.interface';

@Component({
  selector: 'app-form-datetime',
  templateUrl: './datetime.component.html',
  styleUrls: ['./datetime.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: FormDatetimeComponent,
      multi: true,
    },
  ],
})
export class FormDatetimeComponent
  extends FormControlChildAbstract<DateTimeInterface>
  implements OnInit
{
  @Input() public dateErrorMessage: string;
  @Input() public dateLabel?: string;
  @Input() public datePlaceholder?: string;
  @Input() public defaultValue: DateTimeInterface;
  @Input() public errorKey?: string;
  @Input() public isLive: boolean;
  @Input() public maxDate?: string;
  @Input() public maxTime?: string;
  @Input() public minDate?: string;
  @Input() public minTime?: string;
  @Input() public timeErrorMessage: string;
  @Input() public timeLabel?: string;
  @Input() public timePlaceholder?;
  @Input() public timezoneErrorMessage: string;
  @Input() public timezoneLabel?: string;
  @Input() public timezonePlaceholder?: string;
  @Input() public variant?: DatetimeVariantEnum;

  public date: FormControl;
  public isInputDateSupported: boolean;
  public isInputTimeSupported: boolean;
  public time: FormControl;
  public timezone: FormControl;
  public timezones: AutocompleteSuggestion[];

  constructor(
    private readonly paramethorService: ParamethorService,
    private readonly renderer: Renderer2,
    public readonly formControlService: FormControlService
  ) {
    super();
  }

  public ngOnInit(): void {
    if (this.isTimezoneSelectionEnabled || this.isLive) {
      const TzFiltered = this.paramethorService
        .get(ParamethorNameEnum.TimezoneSelection, '')
        .split(',');

      this.timezones = TzFiltered.map(
        (tz: string): AutocompleteSuggestion => ({
          selected: this.timezone?.value === tz,
          text: tz,
          value: tz,
        })
      );
    }

    this.checkSupportedInput();

    this.date = new FormControl(this.defaultValue?.date, [
      dateFormatValidator(),
      dateRangeValidator(
        this.minDate && new Date(this.minDate),
        this.maxDate && new Date(this.maxDate)
      ),
    ]);
    this.time = new FormControl(this.defaultValue?.time, timeFormatValidator());
    this.timezone = new FormControl(this.defaultValue?.timezone);

    this.date.valueChanges.subscribe((value: string) => {
      this.value = {
        ...this.value,
        date: value,
      };
    });

    this.time.valueChanges.subscribe((value: string) => {
      this.value = {
        ...this.value,
        time: value,
      };
    });
    if (this.isTimezoneSelectionEnabled || this.isLive) {
      this.timezone.valueChanges.subscribe((value: string) => {
        this.value = {
          ...this.value,
          timezone: value,
        };
      });
    }
  }

  public get dateErrorMessages(): string[] {
    return (
      this.date?.touched &&
      this.formControlService.getErrorMessages(
        this.date,
        this.errorKey ? `date.${this.errorKey}` : 'date'
      )
    );
  }

  public get isTimezoneSelectionEnabled(): boolean {
    return this.paramethorService.isTrue(ParamethorNameEnum.TimezoneSelection);
  }

  public get timeErrorMessages(): string[] {
    return (
      this.time.touched &&
      this.formControlService.getErrorMessages(
        this.time,
        this.errorKey ? `time.${this.errorKey}` : 'time'
      )
    );
  }

  public get timezoneErrorMessages(): string[] {
    return (
      this.timezone.touched &&
      this.formControlService.getErrorMessages(
        this.timezone,
        this.errorKey ? `timezone.${this.errorKey}` : 'timezone'
      )
    );
  }

  public get inputDateType(): Type {
    return this.isInputDateSupported ? Type.Date : Type.Text;
  }

  public get inputTimeType(): Type {
    return this.isInputTimeSupported ? Type.Time : Type.Text;
  }

  public get variantClass(): string {
    return (
      this.variant === DatetimeVariantEnum.COLUMN &&
      `datetime--${DatetimeVariantEnum.COLUMN}`
    );
  }

  private checkSupportedInput(): void {
    const input = this.renderer.createElement('input');
    const value = 'test';

    input.setAttribute('type', 'date');
    input.setAttribute('value', value);

    this.isInputTimeSupported = input.value !== value;

    input.setAttribute('type', 'time');
    input.setAttribute('value', value);

    this.isInputDateSupported = input.value !== value;
  }
}
